/**
 * InlineForm — collapsible navy2 section used for single-item actions.
 * Preferred over modals per the canonical design language (modals only for
 * consequential bulk actions). Shown below ProfileHeader on the right panel.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { colors, fonts } from './colors';

interface InlineFormProps {
  visible: boolean;
  title?: string;
  onClose?: () => void;
  children: React.ReactNode;
  variant?: 'navy' | 'gold'; // gold variant has gold border for emphasis
}

const InlineForm: React.FC<InlineFormProps> = ({
  visible,
  title,
  onClose,
  children,
  variant = 'navy',
}) => {
  if (!visible) return null;

  const borderColor = variant === 'gold' ? colors.gold : colors.navy3;

  return (
    <div
      style={{
        background: colors.navy2,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '16px 18px',
        margin: '0 24px 16px 24px',
      }}
    >
      {(title || onClose) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
          }}
        >
          {title && (
            <h5
              style={{
                color: colors.gold2,
                fontFamily: fonts.body,
                fontSize: '12px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0,
              }}
            >
              {title}
            </h5>
          )}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.muted,
                cursor: 'pointer',
                padding: '4px 8px',
              }}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

/** Styled input that matches the navy+gold form conventions. */
export const NavyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ style, ...rest }, ref) => (
    <input
      ref={ref}
      {...rest}
      style={{
        background: colors.navy3,
        border: `1px solid ${colors.navy3}`,
        borderRadius: '6px',
        padding: '8px 12px',
        color: colors.white,
        fontSize: '13px',
        outline: 'none',
        width: '100%',
        fontFamily: fonts.body,
        ...style,
      }}
    />
  )
);
NavyInput.displayName = 'NavyInput';

/** Styled textarea that matches the navy+gold form conventions. */
export const NavyTextarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ style, ...rest }, ref) => (
  <textarea
    ref={ref}
    {...rest}
    style={{
      background: colors.navy3,
      border: `1px solid ${colors.navy3}`,
      borderRadius: '6px',
      padding: '8px 12px',
      color: colors.white,
      fontSize: '13px',
      outline: 'none',
      width: '100%',
      fontFamily: fonts.body,
      resize: 'vertical',
      ...style,
    }}
  />
));
NavyTextarea.displayName = 'NavyTextarea';

/** Styled select that matches the navy+gold form conventions. */
export const NavySelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ style, children, ...rest }, ref) => (
  <select
    ref={ref}
    {...rest}
    style={{
      background: colors.navy3,
      border: `1px solid ${colors.navy3}`,
      borderRadius: '6px',
      padding: '8px 12px',
      color: colors.white,
      fontSize: '13px',
      outline: 'none',
      width: '100%',
      fontFamily: fonts.body,
      ...style,
    }}
  >
    {children}
  </select>
));
NavySelect.displayName = 'NavySelect';

export default InlineForm;
